import React, { useState } from "react";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CalendarIcon, MapPin } from "lucide-react";
import { format } from "date-fns";

const BookingSection = () => {
  // Create default dates for Aug 19-20, 2025
  const defaultFromDate = new Date(2025, 7, 19); // Month is 0-indexed, so 7 = August
  const defaultToDate = new Date(2025, 7, 20);
  
  const [dateRange, setDateRange] = useState({
    from: defaultFromDate,
    to: defaultToDate,
  });
  const [destination, setDestination] = useState("");

  const destinations = [
    "The Ritz-Carlton New York, Central Park",
    "The Ritz-Carlton, Paris", 
    "The Ritz-Carlton, Tokyo",
    "The Ritz-Carlton, Dubai",
    "The Ritz-Carlton, Bali",
    "The Ritz-Carlton, London",
    "The Ritz-Carlton, Kapalua Bay",
    "The Ritz-Carlton, Half Moon Bay",
  ];

  const handleSearch = () => {
    console.log("Searching for:", { destination, dateRange });
  };

  const formatDateRange = () => {
    if (dateRange.from && dateRange.to) {
      return `${format(dateRange.from, "EEE, MMM dd")} - ${format(dateRange.to, "EEE, MMM dd")}`;
    } else if (dateRange.from) {
      return format(dateRange.from, "EEE, MMM dd, yyyy");
    }
    return "Select dates";
  };

  const calculateNights = () => {
    if (dateRange.from && dateRange.to) {
      const diffTime = Math.abs(dateRange.to - dateRange.from);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return 0;
  };

  return (
    <section className="pt-2 pb-8 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Single Row Booking Form */}
        <div className="bg-white rounded-lg p-8 max-w-6xl mx-auto">
          
          {/* Horizontal Single Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-end">
            {/* Destination */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-700 uppercase tracking-wide flex items-center">
                <MapPin className="w-3 h-3 mr-1" style={{ color: 'rgb(145, 110, 39)' }} />
                Destination
              </label>
              <Select value={destination} onValueChange={setDestination}>
                <SelectTrigger className="h-12 border-0 border-b-2 border-gray-300 rounded-none focus:border-gray-600 hover:border-gray-400 bg-transparent">
                  <SelectValue placeholder="Where can we take you?" />
                </SelectTrigger>
                <SelectContent>
                  {destinations.map((dest, index) => (
                    <SelectItem key={index} value={dest}>
                      {dest}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range (Number of Nights) */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-700 uppercase tracking-wide flex items-center">
                <CalendarIcon className="w-3 h-3 mr-1" style={{ color: 'rgb(145, 110, 39)' }} />
                {calculateNights() > 0 ? `${calculateNights()} nights` : 'Nights'}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-12 w-full justify-start text-left font-normal border-0 border-b-2 border-gray-300 rounded-none focus:border-gray-600 hover:border-gray-400 bg-transparent p-0"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" style={{ color: 'rgb(145, 110, 39)' }} />
                    <span>{formatDateRange()}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    defaultMonth={dateRange?.from}
                    selected={dateRange}
                    onSelect={setDateRange}
                    numberOfMonths={2}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Find Hotels Button */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-transparent uppercase tracking-wide">
                &nbsp;
              </label>
              <Button
                onClick={handleSearch}
                className="bg-black text-white hover:bg-gray-800 h-12 w-full px-6 text-sm font-medium transition-colors duration-200 rounded-none"
                style={{ 
                  fontFamily: 'Swiss-721, Arial, sans-serif',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  WebkitFontSmoothing: 'antialiased',
                  textRendering: 'optimizeLegibility'
                }}
              >
                Find Hotels
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;